import { collection, addDoc, serverTimestamp, onSnapshot, query, orderBy, deleteDoc, doc, writeBatch, updateDoc } from 'firebase/firestore';
import { db } from './firebase.utils';
import { sanitizeForFirestore } from './serialization';
import { buildLotDescription, stripCardForLot } from '../whatnot/whatnotListingText';
import { mintEbaySkus } from './ebay';
import { isStaged, SALE_KIND, SALE_STATUSES } from '../sales/sales.constants';

const WHATNOT_PLATFORM = 'Whatnot';

const whatnotQueueFields = () => ({
    kind: SALE_KIND,
    platform: WHATNOT_PLATFORM,
    status: SALE_STATUSES.STAGED,
});

/**
 * Adds a single card variation to the user's whatnot staging collection.
 * prefs = { category, subCategory, shippingProfile } — snapshotted at add time.
 */
export async function addCardToWhatnot(uid, cardData, variation, context, prefs = {}) {
    const { sport, year, manufacturer, series, cardType, cardTypeCategory } = context;
    const [sku] = await mintEbaySkus(uid, 1, 'whatnot');

    const docData = sanitizeForFirestore({
        sku,
        // Card identity
        cardNumber:       cardData.cardNumber ?? null,
        name:             cardData.name ?? [],
        team:             cardData.team ?? [],
        cardAttributes:   cardData.cardAttributes ?? [],
        // Variation
        cardParallelName: variation.cardParallelName ?? null,
        cardColor:        variation.cardColor ?? null,
        cardPrintNumber:  variation.cardPrintNumber ?? null,
        cardSupply:       variation.cardSupply ?? null,
        cardOdds:         variation.cardOdds ?? null,
        // Checklist context
        sport:            sport ?? null,
        year:             year ?? null,
        manufacturer:     manufacturer ?? null,
        series:           series ?? null,
        cardType:         cardType ?? null,
        cardTypeCategory: cardTypeCategory ?? null,
        // Export fields (per-listing)
        category:         prefs.category        ?? null,
        subCategory:      prefs.subCategory     ?? null,
        shippingProfile:  prefs.shippingProfile ?? null,
        // Staging metadata
        type:     'single',
        setId:    null,
        ...whatnotQueueFields(),
        addedAt:  serverTimestamp(),
    });

    const colRef = collection(db, 'users', uid, 'transactions');
    const docRef = await addDoc(colRef, docData);
    return docRef.id;
}

/**
 * Adds a custom (on-the-fly) single to the whatnot queue.
 * cardFields = { series, name, cardNumber, cardParallelName, cardPrintNumber, cardSupply, cardColor }
 * prefs      = { category, subCategory, shippingProfile }
 */
export async function addCustomCardToWhatnot(uid, cardFields, prefs = {}) {
    const [sku] = await mintEbaySkus(uid, 1, 'whatnot');

    const docData = sanitizeForFirestore({
        sku,
        cardNumber:       cardFields.cardNumber       ?? null,
        name:             cardFields.name             ?? [],
        team:             [],
        cardAttributes:   [],
        cardParallelName: cardFields.cardParallelName ?? null,
        cardColor:        cardFields.cardColor        ?? null,
        cardPrintNumber:  cardFields.cardPrintNumber  ?? null,
        cardSupply:       cardFields.cardSupply       ?? null,
        sport:            null,
        year:             null,
        manufacturer:     null,
        series:           cardFields.series           ?? null,
        cardType:         null,
        cardTypeCategory: null,
        category:         prefs.category              ?? null,
        subCategory:      prefs.subCategory           ?? null,
        shippingProfile:  prefs.shippingProfile       ?? null,
        type:    'single',
        setId:   null,
        ...whatnotQueueFields(),
        addedAt: serverTimestamp(),
    });

    const colRef = collection(db, 'users', uid, 'transactions');
    const docRef = await addDoc(colRef, docData);
    return docRef.id;
}

/**
 * Subscribes to real-time updates on the user's whatnot staging collection.
 */
export function listenToWhatnotCards(uid, callback) {
    const colRef = collection(db, 'users', uid, 'transactions');
    const q = query(colRef, orderBy('addedAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
        const items = snapshot.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .filter((item) => item.platform === WHATNOT_PLATFORM && isStaged(item.status));
        callback(items);
    }, () => callback([]));
}

/**
 * Removes a single item from the user's whatnot staging collection.
 */
export async function removeCardFromWhatnot(uid, docId) {
    const docRef = doc(db, 'users', uid, 'transactions', docId);
    await deleteDoc(docRef);
}

/**
 * Updates specific fields on a whatnot item (e.g. shippingProfile override).
 */
export async function updateWhatnotItem(uid, docId, data) {
    const docRef = doc(db, 'users', uid, 'transactions', docId);
    await updateDoc(docRef, sanitizeForFirestore(data));
}

/**
 * Combines staged singles into one lot document and removes the source singles.
 * prefs = { category, subCategory, shippingProfile }
 */
export async function createWhatnotLot(uid, title, cardIds, cards, prefs = {}) {
    const colRef = collection(db, 'users', uid, 'transactions');
    const lotRef = doc(colRef);
    const embeddedCards = cards.map(stripCardForLot);
    const [sku] = await mintEbaySkus(uid, 1, 'whatnot');

    const batch = writeBatch(db);
    batch.set(lotRef, sanitizeForFirestore({
        sku,
        type:            'lot',
        title:           title.trim(),
        description:     buildLotDescription(cards),
        cards:           embeddedCards,
        cardCount:       cards.length,
        category:        prefs.category        ?? null,
        subCategory:     prefs.subCategory     ?? null,
        shippingProfile: prefs.shippingProfile ?? null,
        ...whatnotQueueFields(),
        addedAt:         serverTimestamp(),
    }));

    cardIds.forEach((id) => {
        batch.delete(doc(db, 'users', uid, 'transactions', id));
    });

    await batch.commit();
    return lotRef.id;
}

/**
 * Restores a lot's embedded cards as singles and deletes the lot document.
 */
export async function ungroupWhatnotLot(uid, lotItem) {
    const colRef = collection(db, 'users', uid, 'transactions');
    const restoredCards = lotItem.cards ?? [];
    const skus = await mintEbaySkus(uid, Math.max(1, restoredCards.length), 'whatnot');
    const batch = writeBatch(db);

    restoredCards.forEach((card, index) => {
        const singleRef = doc(colRef);
        batch.set(singleRef, sanitizeForFirestore({
            sku: skus[index],
            ...stripCardForLot(card),
            category:        lotItem.category        ?? null,
            subCategory:     lotItem.subCategory     ?? null,
            shippingProfile: lotItem.shippingProfile ?? null,
            type:    'single',
            setId:   null,
            ...whatnotQueueFields(),
            addedAt: serverTimestamp(),
        }));
    });

    batch.delete(doc(db, 'users', uid, 'transactions', lotItem.id));
    await batch.commit();
}
